import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Award,
  LayoutDashboard,
  Users,
  Building2,
  Briefcase,
  BookOpen,
  Trophy,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  Database,
  Shield,
  UserCheck,
  ClipboardCheck,
  Crown,
  GraduationCap,
  HelpCircle,
} from "lucide-react";

// Navigation items with role restrictions
const navigation = [
  { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard, roles: ["super_admin", "admin", "assessor", "iqa"] },
  { name: "Candidates", href: "/candidates", icon: Users, roles: ["super_admin", "admin", "assessor", "iqa"] },
  { name: "Institutions", href: "/institutions", icon: Building2, roles: ["super_admin", "admin"] },
  { name: "IPCB Boards", href: "/ipcbs", icon: Briefcase, roles: ["super_admin", "admin"] },
  { name: "Competencies", href: "/competencies", icon: BookOpen, roles: ["super_admin", "admin"] },
  { name: "Awards", href: "/awards", icon: Trophy, roles: ["super_admin", "admin"] },
  { name: "Analytics", href: "/analytics", icon: BarChart3, roles: ["super_admin", "admin", "assessor", "iqa"] },
  { name: "Users", href: "/users", icon: Shield, roles: ["super_admin", "admin"] },
  { name: "Maintenance", href: "/maintenance", icon: Database, roles: ["super_admin", "admin"] },
  { name: "Badge Settings", href: "/badge-settings", icon: Award, roles: ["super_admin"] },
  { name: "User Guide", href: "/guide", icon: HelpCircle, roles: ["super_admin", "admin", "assessor", "iqa"] },
];

const ROLE_CONFIG = {
  super_admin: { label: "Super Admin", color: "bg-purple-100 text-purple-800", icon: Crown },
  admin: { label: "Admin", color: "bg-blue-100 text-blue-800", icon: Shield },
  assessor: { label: "Assessor", color: "bg-green-100 text-green-800", icon: UserCheck },
  iqa: { label: "IQA", color: "bg-amber-100 text-amber-800", icon: ClipboardCheck },
  industry_specialist: { label: "Industry Specialist", color: "bg-indigo-100 text-indigo-800", icon: Shield },
  candidate: { label: "Candidate", color: "bg-slate-100 text-slate-800", icon: GraduationCap },
};

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // Filter navigation items based on user role
  const userRole = user?.role || "admin";
  const filteredNavigation = navigation.filter(item => item.roles.includes(userRole));
  const roleConfig = ROLE_CONFIG[userRole] || ROLE_CONFIG.admin;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-2 px-4 h-20 border-b border-slate-200">
            <img src="/credentia-ai-logo.png" alt="Credentia AI" className="h-16 w-auto object-contain" />
            <button
              className="ml-auto lg:hidden"
              onClick={() => setSidebarOpen(false)}
              data-testid="close-sidebar-btn"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
            {filteredNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s/g, "-")}`}
                  className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${
                    isActive
                      ? "bg-slate-900 text-white"
                      : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* User */}
          <div className="p-4 border-t border-slate-200">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-3 w-full p-2 rounded-md hover:bg-slate-100 transition-colors"
                  data-testid="user-menu-trigger"
                >
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-slate-200 text-slate-600">
                      {user?.name?.charAt(0) || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {user?.name || "User"}
                    </p>
                    <Badge className={`${roleConfig.color} text-xs mt-0.5`}>
                      {roleConfig.label}
                    </Badge>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem className="text-slate-600">
                  <roleConfig.icon className="w-4 h-4 mr-2" />
                  <span className="text-xs">{roleConfig.label}</span>
                </DropdownMenuItem>
                <DropdownMenuItem className="text-slate-500">
                  <span className="text-xs truncate">{user?.email}</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-red-600 cursor-pointer"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200 h-16 flex items-center px-4 lg:px-8">
          <button
            className="lg:hidden p-2 -ml-2 rounded-md hover:bg-slate-100"
            onClick={() => setSidebarOpen(true)}
            data-testid="open-sidebar-btn"
          >
            <Menu className="w-5 h-5 text-slate-600" />
          </button>
          <div className="flex-1"></div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-500 hidden sm:block">
              {new Date().toLocaleDateString("en-US", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </span>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
